import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { IconComponent } from '@ftr/foundation'
import { SignalledInputs } from '../../util/signalled-inputs'

export type InlineInputSize = 'default' | 'large'

export interface InlineInputArgs {
  value: string
  placeholder: string
  required: boolean
  size: InlineInputSize
  readonly: boolean
  disabled: boolean
  hasError: boolean
  upperCase: boolean
}

@Component({
  selector: 'ftr-inline-input',
  templateUrl: './inline-input.component.html',
  styleUrl: './inline-input.component.css',
  standalone: true,
  imports: [CommonModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineInputComponent implements SignalledInputs<InlineInputArgs> {
  size = input<InlineInputSize>('default')
  value = model('')
  required = input(false)
  disabled = input(false)
  readonly = input(false)
  hasError = input(false)
  upperCase = input(false)
  unfocusedText = input<string>()

  // Inline forms require a placeholder because otherwise the input is not visible to the user
  placeholder = input.required<string>()
  protected label = computed(() => this.placeholder())

  onFocus = output<Event>()
  onBlur = output<Event>()

  private inputElement = viewChild.required<ElementRef<HTMLInputElement>>('inputReference')

  private inputText = signal('')

  protected transformedInputText = computed(() => {
    const inputText = this.inputText()
    const isUppercase = this.upperCase()
    const isFocused = this.isFocused()
    const unfocusedText = this.unfocusedText()
    const hasError = this.hasError()

    if (!isFocused && unfocusedText && !hasError) {
      return unfocusedText
    }

    return isUppercase ? inputText.toUpperCase() : inputText
  })

  /**
   * Replace spaces with non-breaking spaces to ensure they aren't ignored when trying to expand the width.
   * Also, if the input is empty, replace it with at least 1 non-breaking space so that the input has a minimum width.
   */
  protected widthExpanderText = computed(() => this.transformedInputText().replaceAll(' ', '&nbsp;') || '&nbsp;')

  private isFocused = signal(false)

  constructor() {
    effect(
      () => {
        this.inputText.set(this.value())
      },
      { allowSignalWrites: true },
    )
  }

  focus(): void {
    setTimeout(() => this.inputElement()?.nativeElement.focus())
  }

  protected onInput($event: Event): void {
    this.inputText.set(($event.target as HTMLInputElement).value)
  }

  protected onChange(event: Event): void {
    this.value.set((event.target as HTMLInputElement).value)
  }

  handleFocus(e: Event): void {
    this.onFocus.emit(e)
    this.isFocused.set(true)
  }

  handleBlur(e: Event): void {
    this.onBlur.emit(e)
    this.isFocused.set(false)
  }
}
